import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  FileImage, Plus, Search, Upload, Filter, X, Eye,
  Film, FileText, Music, File, Grid3X3,
} from "lucide-react";
import { useState, useRef } from "react";
import type { Asset, Persona, Product, Narrative } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const ASSET_TYPES = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "ugc", label: "UGC" },
  { value: "testimonial", label: "Testimonial" },
  { value: "product_shot", label: "Product Shot" },
  { value: "lifestyle", label: "Lifestyle" },
  { value: "before_after", label: "Before/After" },
  { value: "text", label: "Text/Copy" },
  { value: "audio", label: "Audio" },
  { value: "other", label: "Other" },
];

const PROOF_TYPES = [
  { value: "social_proof", label: "Social Proof" },
  { value: "authority", label: "Authority" },
  { value: "clinical", label: "Clinical/Study" },
  { value: "testimonial", label: "Testimonial" },
  { value: "before_after", label: "Before/After" },
  { value: "demonstration", label: "Demonstration" },
  { value: "user_generated", label: "User Generated" },
  { value: "endorsement", label: "Endorsement" },
];

const FUNNEL_LAYERS = [
  { value: "top", label: "Top of Funnel" },
  { value: "mid", label: "Mid Funnel" },
  { value: "bottom", label: "Bottom of Funnel" },
];

const PERFORMANCE_STATUSES = [
  { value: "untested", label: "Untested" },
  { value: "testing", label: "Testing" },
  { value: "winner", label: "Winner" },
  { value: "fatigued", label: "Fatigued" },
  { value: "retired", label: "Retired" },
];

function assetIcon(type: string | null | undefined) {
  switch (type) {
    case "image":
    case "product_shot":
    case "lifestyle":
    case "before_after":
      return FileImage;
    case "video":
    case "ugc":
      return Film;
    case "text":
      return FileText;
    case "audio":
      return Music;
    default:
      return File;
  }
}

export default function AssetsPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string>("");
  const [filterProof, setFilterProof] = useState<string>("");
  const [filterFunnel, setFilterFunnel] = useState<string>("");
  const [filterPersona, setFilterPersona] = useState<string>("");
  const [filterProduct, setFilterProduct] = useState<string>("");
  const [filterNarrative, setFilterNarrative] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);

  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: assets, isLoading } = useQuery<Asset[]>({
    queryKey: [`/api/brands/${brandId}/assets`],
    enabled: !!brandId,
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  const { data: narratives } = useQuery<Narrative[]>({
    queryKey: [`/api/brands/${brandId}/narratives`],
    enabled: !!brandId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("POST", `/api/brands/${brandId}/assets`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/assets`] });
      setCreateOpen(false);
      setFormValues({});
      toast({ title: "Asset created" });
    },
    onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const handleFileUpload = async (file: globalThis.File) => {
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch("/api/upload", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      const persistedUrl = data.r2Url || data.url || data.falUrl;
      setFormValues(prev => ({ ...prev, fileUrl: persistedUrl, name: prev.name || file.name.replace(/\.[^.]+$/, "") }));
      toast({ title: "File uploaded" });
    } catch (e: any) {
      toast({ title: "Upload failed", description: e.message, variant: "destructive" });
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = () => {
    const payload: Record<string, any> = {
      name: formValues.name,
      assetType: formValues.assetType || null,
      fileUrl: formValues.fileUrl || null,
      transcript: formValues.transcript || null,
      proofType: formValues.proofType || null,
      emotionalTone: formValues.emotionalTone || null,
      usageRights: formValues.usageRights || null,
      performanceStatus: formValues.performanceStatus || "untested",
      notes: formValues.notes || null,
      personaTagsJson: formValues.personaTags ? [formValues.personaTags] : null,
      productTagsJson: formValues.productTags ? [formValues.productTags] : null,
      narrativeTagsJson: formValues.narrativeTags ? [formValues.narrativeTags] : null,
      funnelTagsJson: formValues.funnelTags ? [formValues.funnelTags] : null,
    };
    createMutation.mutate(payload);
  };

  const activeFilterCount = [filterType, filterProof, filterFunnel, filterPersona, filterProduct, filterNarrative, filterStatus].filter(Boolean).length;

  const filtered = (assets || []).filter(asset => {
    if (search && !asset.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType && asset.assetType !== filterType) return false;
    if (filterProof && asset.proofType !== filterProof) return false;
    if (filterStatus && asset.performanceStatus !== filterStatus) return false;
    if (filterFunnel) {
      const funnelTags = asset.funnelTagsJson as string[] | null;
      if (!funnelTags || !funnelTags.includes(filterFunnel)) return false;
    }
    if (filterPersona) {
      const personaTags = asset.personaTagsJson as string[] | null;
      if (!personaTags || !personaTags.includes(filterPersona)) return false;
    }
    if (filterProduct) {
      const productTags = asset.productTagsJson as string[] | null;
      if (!productTags || !productTags.includes(filterProduct)) return false;
    }
    if (filterNarrative) {
      const narrativeTags = asset.narrativeTagsJson as string[] | null;
      if (!narrativeTags || !narrativeTags.includes(filterNarrative)) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setFilterType("");
    setFilterProof("");
    setFilterFunnel("");
    setFilterPersona("");
    setFilterProduct("");
    setFilterNarrative("");
    setFilterStatus("");
  };

  return (
    <>
      <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="page-assets">
        <div className="flex items-center justify-between gap-4 flex-wrap fade-in-up">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-assets-title">Assets</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Asset library with metadata-driven tagging and filtering</p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="rounded-full px-5" data-testid="button-create-asset">
            <Plus className="h-4 w-4 mr-1.5" /> New Asset
          </Button>
        </div>

        <div className="flex items-center gap-3 flex-wrap fade-in-up-delay-1">
          <div className="relative max-w-sm flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search assets..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="input-search-assets"
            />
          </div>
          <Button
            variant={showFilters ? "secondary" : "outline"}
            onClick={() => setShowFilters(!showFilters)}
            className="rounded-full"
            data-testid="button-toggle-filters"
          >
            <Filter className="h-4 w-4 mr-1.5" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="default" className="ml-1.5 text-[10px] rounded-full">{activeFilterCount}</Badge>
            )}
          </Button>
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="button-clear-filters">
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 fade-in-up">
            <div>
              <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1 block">Type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="select-filter-type">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1 block">Proof Type</Label>
              <Select value={filterProof} onValueChange={setFilterProof}>
                <SelectTrigger data-testid="select-filter-proof">
                  <SelectValue placeholder="All proof types" />
                </SelectTrigger>
                <SelectContent>
                  {PROOF_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1 block">Funnel</Label>
              <Select value={filterFunnel} onValueChange={setFilterFunnel}>
                <SelectTrigger data-testid="select-filter-funnel">
                  <SelectValue placeholder="All layers" />
                </SelectTrigger>
                <SelectContent>
                  {FUNNEL_LAYERS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1 block">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="select-filter-status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  {PERFORMANCE_STATUSES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {personas && personas.length > 0 && (
              <div>
                <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1 block">Persona</Label>
                <Select value={filterPersona} onValueChange={setFilterPersona}>
                  <SelectTrigger data-testid="select-filter-persona">
                    <SelectValue placeholder="All personas" />
                  </SelectTrigger>
                  <SelectContent>
                    {personas.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {products && products.length > 0 && (
              <div>
                <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1 block">Product</Label>
                <Select value={filterProduct} onValueChange={setFilterProduct}>
                  <SelectTrigger data-testid="select-filter-product">
                    <SelectValue placeholder="All products" />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {narratives && narratives.length > 0 && (
              <div>
                <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1 block">Narrative</Label>
                <Select value={filterNarrative} onValueChange={setFilterNarrative}>
                  <SelectTrigger data-testid="select-filter-narrative">
                    <SelectValue placeholder="All narratives" />
                  </SelectTrigger>
                  <SelectContent>
                    {narratives.map(n => (
                      <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Card key={i} className="border-border/50"><CardContent className="p-4"><Skeleton className="h-32 w-full rounded-md" /><Skeleton className="h-4 w-3/4 mt-3" /></CardContent></Card>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 fade-in-up-delay-2">
            {filtered.map(asset => {
              const Icon = assetIcon(asset.assetType);
              const isImage = asset.fileUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(asset.fileUrl);
              const funnelTags = asset.funnelTagsJson as string[] | null;
              const personaTags = asset.personaTagsJson as string[] | null;

              return (
                <Card
                  key={asset.id}
                  className="cursor-pointer premium-card group"
                  onClick={() => setPreviewAsset(asset)}
                  data-testid={`card-asset-${asset.id}`}
                >
                  <CardContent className="p-0">
                    <div className="aspect-square bg-muted/50 rounded-t-md flex items-center justify-center relative">
                      {isImage ? (
                        <img
                          src={asset.fileUrl!}
                          alt={asset.name}
                          className="w-full h-full object-cover rounded-t-md"
                          data-testid={`img-asset-${asset.id}`}
                        />
                      ) : (
                        <Icon className="h-10 w-10 text-muted-foreground/50" />
                      )}
                      <div className="absolute top-2 right-2 invisible group-hover:visible">
                        <Button size="icon" variant="secondary" className="rounded-full" data-testid={`button-preview-asset-${asset.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      {asset.performanceStatus && asset.performanceStatus !== "untested" && (
                        <div className="absolute top-2 left-2">
                          <Badge
                            variant={asset.performanceStatus === "winner" ? "default" : "secondary"}
                            className="text-[10px] rounded-full capitalize"
                            data-testid={`badge-status-${asset.id}`}
                          >
                            {asset.performanceStatus}
                          </Badge>
                        </div>
                      )}
                    </div>
                    <div className="p-3 space-y-2">
                      <p className="text-sm font-medium truncate" data-testid={`text-asset-name-${asset.id}`}>{asset.name}</p>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {asset.assetType && (
                          <Badge variant="secondary" className="text-[10px] rounded-full capitalize">{asset.assetType.replace(/_/g, " ")}</Badge>
                        )}
                        {asset.proofType && (
                          <Badge variant="secondary" className="text-[10px] rounded-full capitalize">{asset.proofType.replace(/_/g, " ")}</Badge>
                        )}
                      </div>
                      {funnelTags && funnelTags.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {funnelTags.map(tag => (
                            <Badge key={tag} variant="outline" className="text-[9px] rounded-full capitalize">{tag}</Badge>
                          ))}
                        </div>
                      )}
                      {personaTags && personaTags.length > 0 && personas && (
                        <div className="flex items-center gap-1 flex-wrap">
                          {personaTags.map(tagId => {
                            const p = personas.find(per => per.id === tagId);
                            return p ? <Badge key={tagId} variant="outline" className="text-[9px] rounded-full">{p.name}</Badge> : null;
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-dashed border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Grid3X3 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold tracking-tight mb-1" data-testid="text-empty-assets">No assets yet</h3>
              <p className="text-sm text-muted-foreground">Upload your first asset to get started</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="tracking-tight">Upload Asset</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className="border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors hover:border-primary/50"
              onClick={() => fileInputRef.current?.click()}
              data-testid="dropzone-upload"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) handleFileUpload(file);
                }}
                data-testid="input-file-upload"
              />
              {formValues.fileUrl ? (
                <div className="space-y-2">
                  <img src={formValues.fileUrl} alt="Preview" className="max-h-32 mx-auto rounded-md" data-testid="img-upload-preview" />
                  <p className="text-xs text-muted-foreground">Click to replace</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground/50" />
                  <p className="text-sm text-muted-foreground">
                    {uploadingFile ? "Uploading..." : "Click to upload an image"}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Name *</Label>
              <Input
                value={formValues.name || ""}
                onChange={e => setFormValues(p => ({ ...p, name: e.target.value }))}
                data-testid="input-asset-name"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Asset Type</Label>
              <Select value={formValues.assetType || ""} onValueChange={v => setFormValues(p => ({ ...p, assetType: v }))}>
                <SelectTrigger data-testid="select-asset-type">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Proof Type</Label>
              <Select value={formValues.proofType || ""} onValueChange={v => setFormValues(p => ({ ...p, proofType: v }))}>
                <SelectTrigger data-testid="select-asset-proof-type">
                  <SelectValue placeholder="Select proof type..." />
                </SelectTrigger>
                <SelectContent>
                  {PROOF_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Funnel Layer</Label>
              <Select value={formValues.funnelTags || ""} onValueChange={v => setFormValues(p => ({ ...p, funnelTags: v }))}>
                <SelectTrigger data-testid="select-asset-funnel">
                  <SelectValue placeholder="Select funnel layer..." />
                </SelectTrigger>
                <SelectContent>
                  {FUNNEL_LAYERS.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {personas && personas.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Persona Tag</Label>
                <Select value={formValues.personaTags || ""} onValueChange={v => setFormValues(p => ({ ...p, personaTags: v }))}>
                  <SelectTrigger data-testid="select-asset-persona">
                    <SelectValue placeholder="Tag a persona..." />
                  </SelectTrigger>
                  <SelectContent>
                    {personas.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {products && products.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Product Tag</Label>
                <Select value={formValues.productTags || ""} onValueChange={v => setFormValues(p => ({ ...p, productTags: v }))}>
                  <SelectTrigger data-testid="select-asset-product">
                    <SelectValue placeholder="Tag a product..." />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {narratives && narratives.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Narrative Tag</Label>
                <Select value={formValues.narrativeTags || ""} onValueChange={v => setFormValues(p => ({ ...p, narrativeTags: v }))}>
                  <SelectTrigger data-testid="select-asset-narrative">
                    <SelectValue placeholder="Tag a narrative..." />
                  </SelectTrigger>
                  <SelectContent>
                    {narratives.map(n => (
                      <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Emotional Tone</Label>
              <Input
                value={formValues.emotionalTone || ""}
                onChange={e => setFormValues(p => ({ ...p, emotionalTone: e.target.value }))}
                placeholder="e.g., Warm, Urgent, Aspirational"
                data-testid="input-asset-tone"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Notes</Label>
              <Textarea
                value={formValues.notes || ""}
                onChange={e => setFormValues(p => ({ ...p, notes: e.target.value }))}
                rows={2}
                data-testid="input-asset-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={!formValues.name?.trim() || createMutation.isPending || uploadingFile}
              className="rounded-full"
              data-testid="button-submit-asset"
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewAsset} onOpenChange={open => { if (!open) setPreviewAsset(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {previewAsset && (
            <>
              <DialogHeader>
                <DialogTitle className="tracking-tight" data-testid="text-preview-title">{previewAsset.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {previewAsset.fileUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(previewAsset.fileUrl) && (
                  <div className="rounded-md bg-muted/30">
                    <img
                      src={previewAsset.fileUrl}
                      alt={previewAsset.name}
                      className="max-h-80 mx-auto rounded-md object-contain"
                      data-testid="img-preview-full"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  {previewAsset.assetType && (
                    <div>
                      <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Type</Label>
                      <p className="text-sm capitalize mt-0.5" data-testid="text-preview-type">{previewAsset.assetType.replace(/_/g, " ")}</p>
                    </div>
                  )}
                  {previewAsset.proofType && (
                    <div>
                      <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Proof Type</Label>
                      <p className="text-sm capitalize mt-0.5" data-testid="text-preview-proof">{previewAsset.proofType.replace(/_/g, " ")}</p>
                    </div>
                  )}
                  {previewAsset.emotionalTone && (
                    <div>
                      <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Emotional Tone</Label>
                      <p className="text-sm mt-0.5" data-testid="text-preview-tone">{previewAsset.emotionalTone}</p>
                    </div>
                  )}
                  {previewAsset.performanceStatus && (
                    <div>
                      <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Status</Label>
                      <p className="text-sm capitalize mt-0.5" data-testid="text-preview-status">{previewAsset.performanceStatus}</p>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {(previewAsset.funnelTagsJson as string[] | null)?.map(tag => (
                    <Badge key={tag} variant="outline" className="text-[10px] rounded-full capitalize">{tag}</Badge>
                  ))}
                  {(previewAsset.personaTagsJson as string[] | null)?.map(tagId => {
                    const p = personas?.find(per => per.id === tagId);
                    return p ? <Badge key={tagId} variant="secondary" className="text-[10px] rounded-full">{p.name}</Badge> : null;
                  })}
                  {(previewAsset.productTagsJson as string[] | null)?.map(tagId => {
                    const p = products?.find(pr => pr.id === tagId);
                    return p ? <Badge key={tagId} variant="secondary" className="text-[10px] rounded-full">{p.name}</Badge> : null;
                  })}
                  {(previewAsset.narrativeTagsJson as string[] | null)?.map(tagId => {
                    const n = narratives?.find(nr => nr.id === tagId);
                    return n ? <Badge key={tagId} variant="secondary" className="text-[10px] rounded-full">{n.name}</Badge> : null;
                  })}
                </div>
                {previewAsset.notes && (
                  <div>
                    <Label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Notes</Label>
                    <p className="text-sm text-muted-foreground mt-0.5" data-testid="text-preview-notes">{previewAsset.notes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
